import { Button } from '@repo/videa-components/src/ui/button';
import type { ColumnDef } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useHistory, useLocation } from 'react-router';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { hygieneProviderBenchmarkAtom, isCountsAtom } from '../atoms';
import { formatLargeNumber } from '../helpers/formatNumber';
import type { THygieneRates } from '../types';

import { BenchmarkChip } from './BenchmarkChip';
import { TrendIndicator } from './TrendIndicator';

export type THygieneInsightsTableData = THygieneRates & {
  name: string;
  id: string;
  practiceName?: string;
  providerRole?: string;
};

export const useGetHygieneInsightsTableColumns = ({
  drilldownLevel,
}: {
  drilldownLevel: 'Practice' | 'Provider' | 'Region';
}): ColumnDef<THygieneInsightsTableData, unknown>[] => {
  const isCounts = useAtomValue(isCountsAtom);

  const hygieneProviderBenchmark = useAtomValue(hygieneProviderBenchmarkAtom);

  const history = useHistory();
  const location = useLocation();
  const { trackEvent } = useTrackEvent();

  const handleDrilldown = (id: string, name: string) => {
    trackEvent(mixpanelEvents.insightsView, {
      dashboard: 'HYGIENE',
      drilldownLevel,
      drilldown: true,
      id,
      name,
    });
    let path = location.pathname.split('insights')[0] + 'insights/hygiene/';

    if (drilldownLevel === 'Region') {
      path += 'region/' + id;
    }
    if (drilldownLevel === 'Practice') {
      const regionId = location.pathname.split('region/')[1]?.split('/')[0];

      path += 'region/' + regionId + '/practice/' + id;
    }
    if (drilldownLevel === 'Provider') {
      const regionId = location.pathname.split('region/')[1]?.split('/')[0];
      const practiceId = location.pathname.split('practice/')[1]?.split('/')[0];

      path +=
        'region/' + regionId + '/practice/' + practiceId + '/provider/' + id;
    }

    history.push({ pathname: path, search: location.search });
  };

  const columns: ColumnDef<THygieneInsightsTableData, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Button
          variant="link"
          size="sm"
          onClick={() => handleDrilldown(row.original.id, row.original.name)}
        >
          <span className="vai-font-light vai-underline">
            {row.original.name}
          </span>
        </Button>
      ),
    },
    {
      accessorKey: 'completionRank',
      header: 'Task Completion Rank',
      cell: ({ row }) => {
        const value = row.original.productionRatio;

        return (
          value !== undefined && (
            <BenchmarkChip
              value={value}
              benchmark={hygieneProviderBenchmark}
              isHygiene={true}
            />
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.productionRatio ?? 0) -
          (rowB.original.productionRatio ?? 0)
        );
      },
    },
    {
      accessorKey: 'completionRate',
      header: 'Task Completion Rate',
      cell: ({ row }) => {
        const value = row.original.productionRatio;
        const change = row.original.productionRatioChange;
        return (
          value !== undefined && (
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              {value && Math.round(value * 100)}%
              <TrendIndicator
                value={change && Math.round(change * 100)}
                suffix="%"
              />
            </div>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.productionRatio ?? 0) -
          (rowB.original.productionRatio ?? 0)
        );
      },
    },
    {
      accessorKey: 'totalProduction',
      header: 'Completed Tasks',
      cell: ({ row }) => {
        const value = row.original.production.overallTasks;
        const change = row.original.production.overallTasksChange;
        return (
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            {value && formatLargeNumber(value, isCounts ? '' : '$')}
            <TrendIndicator
              value={change && Math.round(change * 100)}
              suffix="%"
            />
          </div>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.production.overallTasks ?? 0) -
          (rowB.original.production.overallTasks ?? 0)
        );
      },
    },
    {
      accessorKey: 'undiagnosedOpp',
      header: 'Uncompleted Tasks',
      cell: ({ row }) => {
        const value = row.original.opportunity.overallTasks;
        const change = row.original.opportunity.overallTasksChange;
        return (
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            {value && formatLargeNumber(value, isCounts ? '' : '$')}
            <TrendIndicator
              value={change && Math.round(change * 100)}
              suffix="%"
              decreasingIsPositive={true}
            />
          </div>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.opportunity.overallTasks ?? 0) -
          (rowB.original.opportunity.overallTasks ?? 0)
        );
      },
    },
  ];

  if (
    drilldownLevel === 'Provider' &&
    !location.pathname.includes('practice')
  ) {
    const newColumns: ColumnDef<THygieneInsightsTableData, unknown>[] = [
      {
        accessorKey: 'practiceName',
        header: 'Practice',
        cell: ({ row }) => <span>{row.original.practiceName}</span>,
      },
    ];
    columns.splice(1, 0, ...newColumns);
  }

  return columns;
};
